import SkeletonSearchListItem from "../SearchListItem/components/SkeletonSearchListItem";
import styles from "./searchList.module.scss";
import useSearchList, { getSearchPageFromUrl, isPaginatedUrl } from "./useSearchList";
import getFilterActiveStatus, {
  getDistanceFromCityCenterStatus,
  getStayDurationAppliedStatus,
  getUniversityAppliedStatus,
} from "@Pages/Search/utils/getFilterActiveStatus";
import React from "react";
import BookEarlySection from "../BookEarlySection";
import FilterSubsection from "./components/FilterSubsection";
import GetHelpBannerV2 from "./components/GetHelpBannerV2";
import TrustPilotReviewsBanner from "./components/TrustPilotBanner";
import getSubsectionIndex from "./helper";
import FilterSubsection2 from "./components/FilterSubsection2";
import config from "@Config/index";
import RecentelyViewedSection from "../RecentelyViewedSection/RecentelyViewedSection";
import { PAGE } from "constants/configConstants";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import useIsApp from "hooks/useIsApp";
import useIsMiniProgram from "hooks/useIsMiniProgram";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import SearchListItemDesktopV2 from "../SearchListItem/SearchListItemDesktopV2";
import SearchPagination from "../SearchPagination";
import RadiusExpandBanner from "./components/RadiusExpandBanner";
import NearbyResultsHeading from "./components/NearbyResultsHeading";
import RecommendedBeyondFilters from "../RecommendedBeyondFilters";
import {
  getExpandAvailableCount,
  getNearbyResultsCount,
  isRadiusExpandPending,
  shouldShowRadiusExpandBanner,
} from "@Pages/Search/utils/geoRadiusExpand";

interface SearchListDesktopProps {
  showGridView?: boolean;
  isAgentView?: boolean;
  isMapView?: boolean;
}

const SearchListDesktop = ({
  showGridView = false,
  isAgentView = false,
  isMapView = false,
}: SearchListDesktopProps) => {
  const {
    listingRef,
    handleScroll,
    items,
    isLoading,
    meta,
    region,
    filters,
    customLocation,
    whiteLabelPartnerData,
    next,
    isRadiusExpanded,
    radiusExpandSplitIndex,
    radiusExpandEnabled,
    radiusExpandPrimaryCount,
  } = useSearchList();
  const { isWhitelabel, universityMock } = useWhitelabel();
  const isApp = useIsApp();
  const isMiniProgram = useIsMiniProgram();

  const isFiltersSubsectionEnabled = true;
  const areFiltersApplied = getFilterActiveStatus("Filters", filters);
  const areFiltersAppliedV2 =
    getDistanceFromCityCenterStatus(filters) ||
    getUniversityAppliedStatus(filters) ||
    getStayDurationAppliedStatus(filters);
  const nearbyResultsCount = getNearbyResultsCount({
    metaCount: meta?.count,
    primaryCount: radiusExpandPrimaryCount,
    itemsLength: items?.length || 0,
    splitIndex: radiusExpandSplitIndex,
    filters,
    region,
    enabled: radiusExpandEnabled,
    isChinaApp: Boolean(config.IS_CHINA_APP),
    isWhitelabel: Boolean(config.IS_WHITELABEL_APP || isWhitelabel),
    isApp,
    isMiniProgram,
  });
  const radiusExpandPending = isRadiusExpandPending({
    isExpanded: isRadiusExpanded,
    isLoading,
    itemsLength: items?.length || 0,
    splitIndex: radiusExpandSplitIndex,
  });
  // Keep the CTA banner in place while expand is loading; only swap to the
  // divider + nearby heading once appended inventories have landed.
  // Map sidebar keeps the empty/no-matches CTA; hide mid-list expand UI while map is open.
  const showRadiusExpandDivider =
    !isMapView && isRadiusExpanded && radiusExpandSplitIndex > 0 && !radiusExpandPending;
  const bannerEligible =
    radiusExpandPending ||
    shouldShowRadiusExpandBanner({
      filters,
      region,
      itemsLength: items?.length || 0,
      isLoading,
      isExpanded: isRadiusExpanded,
      metaCount: meta?.count,
      expandAvailableCount: getExpandAvailableCount(meta),
      next,
      isPaginated: isPaginatedUrl(),
      currentPage: getSearchPageFromUrl(),
      hasNonGeoFilters: areFiltersApplied,
      enabled: radiusExpandEnabled,
    });
  const showRadiusExpandBanner = bannerEligible && (!isMapView || (items?.length || 0) === 0);

  return (
    <div
      className={formatClassNames(styles.containerDesktop, showGridView && styles.gridView)}
      ref={listingRef}
      onScroll={handleScroll}
    >
      {items?.map((item: any, index: number) => {
        const {
          shouldShowGetHelpBanner,
          shouldShowTrustPilot,
          shouldShowBookEarlySection,
          shouldShowFilters,
          shouldShowFiltersV2,
          shouldShowRecentlyViewdSection,
        } = getSubsectionIndex({
          areFiltersApplied,
          areFiltersAppliedV2,
          resultCount: meta?.count,
          index,
          isFiltersSubsectionEnabled,
        });
        return (
          <React.Fragment key={`${item.key}_${index}`}>
            {showRadiusExpandDivider && index === radiusExpandSplitIndex && (
              <div
                id="radius-expand-nearby-anchor"
                className={formatClassNames(
                  styles.nearbyAnchor,
                  showGridView && styles.fullWidthRow,
                )}
              >
                <RadiusExpandBanner showCard={false} />
                {nearbyResultsCount > 0 && (
                  <NearbyResultsHeading count={nearbyResultsCount} isDesktop />
                )}
              </div>
            )}
            {!isWhitelabel && !isMapView && (
              <>
                {shouldShowGetHelpBanner && <GetHelpBannerV2 isDesktop />}
                {isFiltersSubsectionEnabled && shouldShowFilters && <FilterSubsection isDesktop />}
              </>
            )}

            {!(isWhitelabel && universityMock) && (
              <SearchListItemDesktopV2
                index={index}
                {...item}
                regionType={region?.region_type}
                regionId={region?.id}
                region={region}
                meta={item.meta_short}
                meta_real={item.meta}
                page={PAGE.SEARCH}
                loadedCount={items.length}
                isAgentView={isAgentView}
                customLocation={customLocation}
                whiteLabelPartnerData={whiteLabelPartnerData}
              />
            )}

            {!isWhitelabel && !isMapView && isFiltersSubsectionEnabled && shouldShowFiltersV2 && (
              <FilterSubsection2 isDesktop />
            )}

            {!isWhitelabel && !isMapView && shouldShowRecentlyViewdSection && (
              <RecentelyViewedSection
                isSearchPage
                isSearchPageRails
                gtagEventCategory={PAGE.SEARCH}
                isDesktop
              />
            )}

            {!isWhitelabel && !isMapView && shouldShowTrustPilot && !config.IS_CHINA_APP && (
              <TrustPilotReviewsBanner isSearchPage isDesktop uniCity={null} />
            )}
            {!isWhitelabel && !isMapView && shouldShowBookEarlySection && (
              <BookEarlySection isDesktop />
            )}
          </React.Fragment>
        );
      })}

      {showRadiusExpandDivider && (items?.length || 0) <= radiusExpandSplitIndex && (
        <div
          id="radius-expand-nearby-anchor"
          className={formatClassNames(styles.nearbyAnchor, showGridView && styles.fullWidthRow)}
        >
          <RadiusExpandBanner showCard={false} />
          {nearbyResultsCount > 0 && <NearbyResultsHeading count={nearbyResultsCount} isDesktop />}
        </div>
      )}
      {(isLoading || universityMock) &&
        !radiusExpandPending &&
        [...new Array(10)].map((_it, index) => (
          <div
            key={`loading_${index}`}
            style={{ viewTransitionName: `search-item-skeleton-${index}` }}
          >
            <SkeletonSearchListItem isDesktop={!showGridView} />
          </div>
        ))}

      {/* SSR Trustpilot: render after list if inline position (index 17) wasn't reached */}
      {!isWhitelabel &&
        !isMapView &&
        !config.IS_CHINA_APP &&
        items?.length > 0 &&
        items.length <= 17 && <TrustPilotReviewsBanner isSearchPage isDesktop uniCity={null} />}

      {showRadiusExpandBanner && (
        <div className={showGridView ? styles.fullWidthRow : undefined}>
          <RadiusExpandBanner compact={isMapView} />
        </div>
      )}

      {/* Recommended properties beyond the exact filters — client-only, renders once exhausted.
          Deliberately not gated on the `isAgentView` prop: Search/Desktop/index.tsx passes
          `isAgentView={isInternalUser}`, so that prop means "internal user", not "agent view is
          open", and gating on it hid the section from every internal user. The real agent-view
          state is `isAgentViewOpened` in the store, which useRecommendations already checks. */}
      <div className={showGridView ? styles.fullWidthRow : undefined}>
        <RecommendedBeyondFilters isDesktop={!showGridView} isGridView={showGridView} />
      </div>

      {/* Pagination block */}
      {items?.length > 0 && <SearchPagination />}
    </div>
  );
};

export default wrapperHOC(SearchListDesktop, {
  componentName: "SearchListDesktop-SearchPage",
  showForChina: true,
});
